import { AnimatePresence, m } from "framer-motion";
import { Fragment } from "react";
import { opacityAnimation } from "@ui/animation/opacity-animation";
import { Skeleton } from "@ui/skeleton/skeleton";
import { useProducts } from "@common/billing/pricing-table/use-products";
import { Product } from "@common/billing/product";
import {
  findBestPrice,
  UpsellBillingCycle,
} from "@common/billing/pricing-table/find-best-price";
import { useAuth } from "@common/auth/use-auth";
import clsx from "clsx";
import { Chip } from "@ui/forms/input-field/chip-field/chip";
import { Trans } from "@ui/i18n/trans";
import { FormattedPrice } from "@common/billing/formatted-price";
import { Button } from "@ui/buttons/button";
import { Link } from "react-router-dom";
import { setInLocalStorage } from "@ui/utils/hooks/local-storage";
import { ProductFeatureList } from "@common/billing/pricing-table/product-feature-list";

interface PricingTableProps {
  selectedCycle: UpsellBillingCycle;
  className?: string;
  productLoader?: string;
}
export function PricingTable({
  selectedCycle,
  className,
  productLoader,
}: PricingTableProps) {
  const query = useProducts(productLoader);
  return (
    <div className={clsx("pb-20", className)}>
      <AnimatePresence initial={false} mode="wait">
        {query.data ? (
          <PlanList
            key="plan-list"
            plans={query.data.products}
            selectedPeriod={selectedCycle}
          />
        ) : (
          <SkeletonLoader key="skeleton-loader" />
        )}
      </AnimatePresence>
    </div>
  );
}

interface PlanListProps {
  plans: Product[];
  selectedPeriod: UpsellBillingCycle;
}
function PlanList({ plans, selectedPeriod }: PlanListProps) {
  const filteredPlans = plans.filter((plan) => !plan.hidden);
  const creatorPlans = filteredPlans.filter((plan) =>
    ["IEST Artist", "4Kreator Pro"].includes(plan.name),
  );
  const listenerPlans = filteredPlans.filter(
    (plan) => !creatorPlans.includes(plan),
  );

  return (
    <m.div {...opacityAnimation} className="space-y-64">
      <PlanGroup
        eyebrow="For listeners"
        title="Listen, discover and support independent music"
        description="Start free, then upgrade for an advertisement-free experience and member exclusives."
        plans={listenerPlans}
        selectedPeriod={selectedPeriod}
      />
      <PlanGroup
        eyebrow="For creators"
        title="Create, grow and build your audience"
        description="Artist registration is free and approval-based. Upgrade when you need more publishing capacity and creator tools."
        plans={creatorPlans}
        selectedPeriod={selectedPeriod}
      />
    </m.div>
  );
}

interface PlanGroupProps {
  eyebrow: string;
  title: string;
  description: string;
  plans: Product[];
  selectedPeriod: UpsellBillingCycle;
}
function PlanGroup({
  eyebrow,
  title,
  description,
  plans,
  selectedPeriod,
}: PlanGroupProps) {
  if (!plans.length) return null;
  return (
    <section>
      <div className="mx-auto mb-28 max-w-700 text-center">
        <div className="mb-8 text-xs font-semibold uppercase tracking-widest text-primary">
          <Trans message={eyebrow} />
        </div>
        <h2 className="mb-10 text-2xl font-semibold md:text-3xl">
          <Trans message={title} />
        </h2>
        <p className="text-sm text-muted md:text-base">
          <Trans message={description} />
        </p>
      </div>
      <div className="mx-auto grid max-w-800 items-stretch gap-24 md:grid-cols-2">
        {plans.map((plan) => (
          <PlanCard key={plan.id} plan={plan} selectedPeriod={selectedPeriod} />
        ))}
      </div>
    </section>
  );
}

interface PlanCardProps {
  plan: Product;
  selectedPeriod: UpsellBillingCycle;
}
function PlanCard({ plan, selectedPeriod }: PlanCardProps) {
  const { isLoggedIn, isSubscribed } = useAuth();
  const price = findBestPrice(selectedPeriod, plan.prices);
  const isArtistPlan = plan.name === "IEST Artist";
  let upgradeRoute: string | undefined;

  if (isArtistPlan) {
    upgradeRoute = "/creator-register";
  } else if (!isLoggedIn) {
    upgradeRoute = "/register?redirectFrom=pricing";
  } else if (!plan.free && isSubscribed) {
    upgradeRoute = `/change-plan/${plan.id}/${price?.id}/confirm`;
  } else if (!plan.free) {
    upgradeRoute = `/checkout/${plan.id}/${price?.id}`;
  }

  const buttonLabel = isArtistPlan
    ? "Join as an artist"
    : plan.free
      ? isLoggedIn
        ? "Current plan"
        : "Get started"
      : isSubscribed
        ? "Change plan"
        : "Upgrade";

  return (
    <div className="flex w-full flex-col rounded-panel border bg px-28 py-28 shadow-lg">
      <div className="mb-32">
        <Chip
          radius="rounded"
          size="sm"
          className={clsx("mb-20 w-min", !plan.recommended && "invisible")}
        >
          <Trans
            message={
              plan.name === "4Kreator Pro" ? "Creator favorite" : "Most popular"
            }
          />
        </Chip>
        <div className="mb-12 text-xl font-semibold">
          <Trans message={plan.name} />
        </div>
        <div className="text-sm text-muted">
          <Trans message={plan.description} />
        </div>
      </div>
      <div className="flex flex-1 flex-col">
        {price ? (
          <FormattedPrice
            priceClassName="font-bold text-4xl"
            periodClassName="text-muted text-xs"
            variant="separateLine"
            price={price}
          />
        ) : (
          <div className="text-4xl font-bold">
            <Trans message="Free" />
          </div>
        )}
        <div className="mt-60">
          <Button
            variant={plan.recommended ? "flat" : "outline"}
            color="primary"
            className="w-full"
            size="md"
            elementType={upgradeRoute ? Link : undefined}
            disabled={!upgradeRoute}
            onClick={() => {
              if (isLoggedIn || !price) return;
              setInLocalStorage("be.onboarding.selected", {
                productId: plan.id,
                priceId: price.id,
              });
            }}
            to={upgradeRoute}
          >
            <Trans message={buttonLabel} />
          </Button>
        </div>
        <ProductFeatureList product={plan} />
      </div>
    </div>
  );
}

function SkeletonLoader() {
  return (
    <Fragment>
      <PlanSkeleton key="skeleton-1" />
      <PlanSkeleton key="skeleton-2" />
      <PlanSkeleton key="skeleton-3" />
    </Fragment>
  );
}

function PlanSkeleton() {
  return (
    <m.div
      {...opacityAnimation}
      className="w-full rounded-lg border px-28 py-90 shadow-lg md:max-w-350"
    >
      <Skeleton className="my-10" />
      <Skeleton className="mb-40" />
      <Skeleton className="mb-40 h-30" />
      <Skeleton className="mb-40 h-40" />
      <Skeleton className="mb-20" />
      <Skeleton />
      <Skeleton />
    </m.div>
  );
}
